import dayjs from "dayjs";

import { IEvent, IGallery, IGalleryItem, ILogin, ISourceItem, ITrackDistances, ITrackItem, ITrackListItem } from "~/interfaces";
import { getDataPath } from "~/providers/php-api";
import { getPersonsData } from "~/persons/persons";
import { DAYJS_FORMAT } from "~/const-files/const";
import { createEmptyDistances, isLocalhost } from "./utils";

interface IGalleryData {
	meta: {
		galleryTitles: string;
		prologItems: Array<IGalleryItem>;
		epilogItems: Array<IGalleryItem>;
		othersItems: Array<IGalleryItem>;
		datesObjDays: {[key: string]: Array<IGalleryItem>};
	};
	gallery: IGallery;
}

interface ICreateGallery {
	event: IEvent;
	folder: string;
	isYear: boolean;
	type: ILogin["type"];
	// konfigurace fotek podle seo udalosti
	sourceConfs: Map<string, ISourceItem["conf"]>;
}

// klic pro polozky bez datumu v datesObjDays
export const NONE_DATE_KEY = "none";

// souhrnna data galerie (rozsah dat, pocty, osoby, titulky) jsou predpocitana v buildu - scripts/events-groups.ts
export function createGallery({
	event,
	folder,
	isYear,
	type,
	sourceConfs,
}: ICreateGallery): IGalleryData | null {
	if (!event.galleryEvent) {
		return null;
	}

	// mame galerii
	const noneDateKey = NONE_DATE_KEY;
	const { stats } = event.galleryEvent;
	const items: Array<IGalleryItem> = [];
	const prologItems: IGalleryData["meta"]["prologItems"] = [];
	const datesObjDays: IGalleryData["meta"]["datesObjDays"] = {};
	const epilogItems: IGalleryData["meta"]["epilogItems"] = [];
	const filteredSource = sourceConfs.get(event.seo) || {};
	const isMonth = event.galleryEvent.type === "month-item";
	const localhost = isLocalhost();

	event.galleryEvent.items.forEach(galleryItem => {
		const updatedItem: IGalleryItem = {
			...galleryItem,
			src: getDataPath(galleryItem.isVideo ? "videa" : "fotky", `${folder}/${event.galleryEvent.src}/${galleryItem.name}`, type),
			eventSeo: event.seo,
			coordsSource: galleryItem.coords ? "image" : "none",
			dateSource: galleryItem.date ? "image" : "none",
		};

		if (galleryItem.isVideo) {
			updatedItem.preview = getDataPath("fotky", `${folder}/${event.galleryEvent.src}/${galleryItem.videoCover || galleryItem.name.replace(".mp4", "-cover.jpg")}`, type);
		} else {
			updatedItem.largeSrc = getDataPath("fotky", `${folder}/${event.galleryEvent.src}/${galleryItem.name.replace(".", "-large.")}`, type);
		}

		if (event.galleryEvent.cover === galleryItem.name) {
			updatedItem.isCover = true;
		}

		if (galleryItem.name in filteredSource) {
			updatedItem.meta = filteredSource[galleryItem.name];

			if (updatedItem.meta.coords) {
				updatedItem.coordsSource = "manual";
			}
		}

		// pouzijeme souradnice z rodice pro fotky
		if (!updatedItem.coords && event.coords && !isMonth) {
			updatedItem.coords = event.coords;
			updatedItem.coordsSource = "event";
		}

		if (!updatedItem.date && !event.toDate && !isMonth) {
			updatedItem.date = event.date;
			updatedItem.dateSource = "event";

			if (updatedItem.meta?.date) {
				updatedItem.dateSource = "manual";
			}
		}

		if (galleryItem.persons) {
			if (localhost) {
				// @ts-expect-error typ
				const origPersons: Array<string> = galleryItem.persons;
				const checkArray = [...new Set(origPersons)];

				if (origPersons.length !== checkArray.length) {
					/* eslint-disable */
					console.log(["duplicity person", updatedItem.eventSeo, updatedItem.name, origPersons.join(", ")]);
				}
			}

			// @ts-expect-error typ
			updatedItem.persons = getPersonsData(galleryItem.persons);
		}

		const dateKey = galleryItem.date || noneDateKey;
		const dateMatch = dateKey.match(/\d{4}-\d{2}-\d{2}/u);
		const onlyDate = galleryItem.customDayName
			? galleryItem.customDayName
			: dateMatch
				? dateMatch[0]
				: dateKey;
		// zdroj
		let source: Array<IGalleryItem> = [];

		if (updatedItem.customDayName === "prolog") {
			source = prologItems;
		} else if (updatedItem.customDayName === "epilog") {
			source = epilogItems;
		} else {
			if (!(onlyDate in datesObjDays)) {
				datesObjDays[onlyDate] = [];
			}

			source = datesObjDays[onlyDate];
		}

		source.push(updatedItem);
		items.push(updatedItem);
	});

	// jiz priradeny event
	const newGallery: IGallery = {
		cover: getDataPath("fotky", `${folder}/${event.galleryEvent.src}/${event.galleryEvent.cover}`, type),
		dateType: event.galleryEvent.dateType,
		datesRangeFrom: stats.datesRangeFrom,
		datesRangeTo: stats.datesRangeTo,
		type: event.galleryEvent.type,
		items,
		photosCount: stats.photosCount,
		videosCount: stats.videosCount,
		event,
		galleryType: isYear ? "year" : "other",
		xCor: event.galleryEvent.xCor,
		persons: getPersonsData(stats.persons),
		folderSrc: event.galleryEvent.src,
	};

	// do eventu taky priradime
	event.gallery = newGallery;

	// reverse pro mesice
	if (newGallery.type === "reverse-by-date" || isMonth) {
		const monthDate = dayjs().set("date", 1).format(DAYJS_FORMAT);

		if (monthDate === newGallery.event.date) {
			newGallery.items.reverse();
		}
	}

	return {
		meta: {
			galleryTitles: stats.titles,
			prologItems,
			epilogItems,
			datesObjDays,
			othersItems: noneDateKey in datesObjDays ? datesObjDays[noneDateKey] : [],
		},
		gallery: newGallery,
	};
}

export function calculateTrackDistances(track: ITrackItem) {
	// do all pocitame pouze pesky + auto?
	track.distances = createEmptyDistances();

	for (const geometry of track.geometries) {
		switch (geometry.type) {
			case "walk":
				track.distances.walk += geometry.distance;
				track.distances.all += geometry.distance;
				break;

			case "car":
				track.distances.car += geometry.distance;
				track.distances.all += geometry.distance;
				break;

			case "boat":
				track.distances.boat += geometry.distance;
				break;

			case "bike":
				track.distances.bike += geometry.distance;
				track.distances.all += geometry.distance;
				break;

			case "cable":
				track.distances.cable += geometry.distance;
				break;

			case "pubt":
				track.distances.pubt += geometry.distance;
				break;

			case "train":
				track.distances.train += geometry.distance;
				break;

			default:
		}
	}
}

export function mergeDistances(distancesSource: ITrackDistances, distancesDest: ITrackDistances) {
	distancesSource.walk += distancesDest.walk;
	distancesSource.car += distancesDest.car;
	distancesSource.boat += distancesDest.boat;
	distancesSource.cable += distancesDest.cable;
	distancesSource.pubt += distancesDest.pubt;
	distancesSource.bike += distancesDest.bike;
	distancesSource.train += distancesDest.train;
	distancesSource.all += distancesDest.all;
}

export function mergeDistancesWithTracks(distances: ITrackDistances, tracks: Array<ITrackItem>) {
	tracks.forEach(track => {
		mergeDistances(distances, track.distances);
	});
}

export function createTrackListItem(event: IEvent, gallery: IGallery, tracks: Array<ITrackItem>): ITrackListItem {
	const trackListItem: ITrackListItem = {
		tracks: tracks,
		event,
		gallery,
		distances: createEmptyDistances(),
	};

	mergeDistancesWithTracks(trackListItem.distances, tracks);

	return trackListItem;
}
